// Pending approvals list view. fn-1.29.
//
// **Acceptance**: "Approvals list shows each pending action's tool
// name + args (sanitized); approve and reject buttons round-trip to
// the control plane in < 1s perceived latency."
//
// **Wiring**:
//   - Source: `trpc.approvals.list.useQuery()` (BFF proxy over
//     `GET /approvals`).
//   - Polling at 3s so a new pending appears quickly + decisions show
//     resolved promptly.
//   - Approve / Reject: `trpc.approvals.decide.useMutation()`. On
//     success: invalidate the badge query so the count drops without
//     waiting for the next 5s tick.
//
// **Sanitization note**: we render `tool_args` as a pre-formatted JSON
// blob. The control plane is responsible for redacting secrets BEFORE
// the row lands in `approvals_audit` — if a tool's input ever carries
// a secret (e.g. an API key), that's an upstream bug, not a
// presentation bug. We do NOT do client-side scrubbing because the
// risk is "data already in the database is sensitive"; scrubbing on
// render would create a false sense of security.

import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { useClientOtel, useTrackUserAction } from '../../instrumentation/provider.js';
import { trpc } from '../../trpc-client.js';
import { formatTime, truncateToolName } from '../format.js';

const POLL_MS = 3_000;
/**
 * fn-2.x — how long a freshly-decided row stays visible on the list
 * after the operator clicks Approve / Reject. Per dogfood feedback:
 * "Once we approve, it should probably transition to an approved
 * status but not disappear off the screen just yet — that way, you
 * can then decide to jump into the session if you want."
 */
const DECIDED_PERSIST_MS = 30_000;

interface DecidedRecord {
  readonly row: ApprovalListRow;
  readonly decision: 'approved' | 'rejected';
  readonly decidedAt: number;
}

interface ApprovalListRow {
  readonly id: string;
  readonly session_id: string;
  readonly tool_kind: 'builtin' | 'mcp' | 'custom';
  readonly tool_name: string;
  readonly tool_args: Record<string, unknown>;
  readonly ack_kind:
    | 'tool_confirmation'
    | 'custom_tool_result_immediate'
    | 'custom_tool_result_deferred';
  readonly created_at: string;
}

export function ApprovalsListPage(): React.ReactElement {
  const listQuery = trpc.approvals.list.useQuery(
    {},
    {
      refetchInterval: POLL_MS,
    },
  );

  // Local "recently decided" cache. Keyed by approval id; values carry
  // the original row + the operator's decision + decision timestamp so
  // we can render an Approved/Rejected chip in place of the action
  // buttons. The server-side list drops these rows immediately (the
  // CP filters to pending only); we union them back in so they stay
  // visible for DECIDED_PERSIST_MS.
  const [decided, setDecided] = useState<Map<string, DecidedRecord>>(() => new Map());

  // Evict decided rows past their TTL. Runs once per second while any
  // are tracked; idle otherwise.
  useEffect(() => {
    if (decided.size === 0) return;
    const interval = setInterval(() => {
      const now = Date.now();
      setDecided((prev) => {
        let mutated = false;
        const next = new Map(prev);
        for (const [id, rec] of prev) {
          if (now - rec.decidedAt >= DECIDED_PERSIST_MS) {
            next.delete(id);
            mutated = true;
          }
        }
        return mutated ? next : prev;
      });
    }, 1_000);
    return (): void => clearInterval(interval);
  }, [decided.size]);

  const recordDecision = (row: ApprovalListRow, decision: 'approved' | 'rejected'): void => {
    setDecided((prev) => {
      const next = new Map(prev);
      next.set(row.id, { row, decision, decidedAt: Date.now() });
      return next;
    });
  };

  const merged = useMemo(() => {
    const serverRows = (listQuery.data?.data ?? []) as readonly ApprovalListRow[];
    const seen = new Set<string>();
    const out: Array<{ row: ApprovalListRow; decided?: DecidedRecord }> = [];
    for (const row of serverRows) {
      seen.add(row.id);
      const rec = decided.get(row.id);
      out.push(rec !== undefined ? { row, decided: rec } : { row });
    }
    // Append decided-but-no-longer-on-server rows.
    for (const rec of decided.values()) {
      if (seen.has(rec.row.id)) continue;
      out.push({ row: rec.row, decided: rec });
    }
    return out;
  }, [listQuery.data, decided]);

  if (listQuery.isPending) {
    return (
      <section className="page page-approvals" aria-busy="true">
        <h1>Pending approvals</h1>
        <p>Loading…</p>
      </section>
    );
  }
  if (listQuery.error !== null) {
    return (
      <section className="page page-approvals" role="alert">
        <h1>Pending approvals</h1>
        <p>Failed to load approvals: {listQuery.error.message}</p>
      </section>
    );
  }

  return (
    <section className="page page-approvals">
      <h1>Pending approvals</h1>
      <p style={{ color: 'var(--sb-text-muted)', margin: '0.25rem 0 1rem' }}>
        Tools waiting for human review. Click Approve to let the agent proceed, or Reject to deny.
        Decided rows stay visible for {Math.round(DECIDED_PERSIST_MS / 1000)}s so you can jump into
        the session.
      </p>
      {merged.length === 0 ? (
        <p style={{ color: 'var(--sb-text-muted)' }}>No pending approvals.</p>
      ) : (
        <ul className="approvals-list" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {merged.map(({ row, decided: rec }) => (
            <ApprovalRow
              key={row.id}
              approvalId={row.id}
              sessionId={row.session_id}
              toolKind={row.tool_kind}
              toolName={row.tool_name}
              toolArgs={row.tool_args}
              ackKind={row.ack_kind}
              createdAt={row.created_at}
              {...(rec !== undefined ? { decidedRecord: rec } : {})}
              onDecided={(decision) => recordDecision(row, decision)}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

interface ApprovalRowProps {
  readonly approvalId: string;
  readonly sessionId: string;
  readonly toolKind: 'builtin' | 'mcp' | 'custom';
  readonly toolName: string;
  readonly toolArgs: Record<string, unknown>;
  readonly ackKind:
    | 'tool_confirmation'
    | 'custom_tool_result_immediate'
    | 'custom_tool_result_deferred';
  readonly createdAt: string;
  /**
   * fn-2.x — when the operator has already decided this row, carries
   * the decision + timestamp. Renders an Approved/Rejected chip in
   * place of the action buttons; the row sticks around for
   * DECIDED_PERSIST_MS so the operator can still click through to the
   * session.
   */
  readonly decidedRecord?: DecidedRecord;
  /**
   * fn-2.x — called immediately after a successful decide-mutation so
   * the parent can register the decision in its local cache. The
   * server-side list drops the row on the next poll; we union the
   * decided record back in to keep it visible.
   */
  readonly onDecided?: (decision: 'approved' | 'rejected') => void;
}

function ApprovalRow(props: ApprovalRowProps): React.ReactElement {
  const decideMutation = trpc.approvals.decide.useMutation();
  const utils = trpc.useUtils();
  const trackUserAction = useTrackUserAction();
  const otel = useClientOtel();
  const isDecided = props.decidedRecord !== undefined;
  // fn-1.31 round 6 — prime the platform-sessionId trace-id cache so
  // the click handler's `trackUserAction({ sessionId })` emits
  // synchronously on the right trace (no async hash on the click
  // path). Fire-and-forget; idempotent.
  useEffect(() => {
    if (otel !== null) {
      void otel.primeSessionTrace(props.sessionId);
    }
  }, [otel, props.sessionId]);

  const handle = async (decision: 'approved' | 'rejected', denyMessage?: string): Promise<void> => {
    // fn-1.31 — emit a SPA-side Langfuse span for the click. The
    // span's id becomes the parent-id of the outbound traceparent
    // the wrapped tRPC fetch carries to the BFF, which is how the
    // BFF's `bff.approvals.decide` span nests under this one.
    trackUserAction({
      name: `spa.approval.${decision === 'approved' ? 'approve' : 'reject'}`,
      // fn-1.31 round 6 — anchor on the platform sessionId so the SPA
      // click span + the BFF tRPC span + the control-plane decision
      // span ALL land on the same Langfuse trace
      // (`hash(platformSessionId)`). Without this, the SPA click is
      // emitted on `hash(userId)` while the CP emits on
      // `hash(platformSessionId)` — two split traces for one logical
      // approval flow.
      sessionId: props.sessionId,
      metadata: {
        approval_id: props.approvalId,
        session_id: props.sessionId,
        tool_name: props.toolName,
        tool_kind: props.toolKind,
      },
    });
    try {
      await decideMutation.mutateAsync({
        sessionId: props.sessionId,
        approvalId: props.approvalId,
        decision,
        ...(denyMessage !== undefined && denyMessage.length > 0 ? { denyMessage } : {}),
      });
      // fn-2.x — record the decision locally so the row sticks around
      // with an Approved/Rejected chip even after the server-side list
      // drops it. Only the badge count is invalidated immediately —
      // the list itself is allowed to refresh on its 3s poll cycle, by
      // which point the parent's local cache renders the decided row.
      props.onDecided?.(decision);
      await utils.approvals.pendingCount.invalidate();
    } catch {
      // surfaces via decideMutation.error
    }
  };

  return (
    <li
      className={`approvals-list-row${isDecided ? ` approvals-list-row--decided approvals-list-row--${props.decidedRecord?.decision}` : ''}`}
      data-approval-id={props.approvalId}
      style={{
        background: 'var(--sb-bg-panel)',
        border: '1px solid var(--sb-border)',
        borderRadius: '8px',
        padding: '0.85rem 1rem',
        marginBottom: '0.65rem',
        ...(isDecided ? { opacity: 0.78 } : {}),
      }}
    >
      <header
        className="approvals-list-row-header"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          marginBottom: '0.5rem',
          flexWrap: 'wrap',
        }}
      >
        <span
          className="sb-chip"
          style={{ textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: '0.05em' }}
        >
          {props.toolKind}
        </span>
        <span
          style={{ fontFamily: 'var(--sb-font-mono)', fontSize: '0.9rem', fontWeight: 500 }}
          title={props.toolName}
        >
          {truncateToolName(props.toolName)}
        </span>
        <span className="sb-chip" style={{ fontSize: '0.72rem' }}>
          {props.ackKind}
        </span>
        <Link
          to={`/runs/${encodeURIComponent(props.sessionId)}`}
          style={{
            fontFamily: 'var(--sb-font-mono)',
            fontSize: '0.82rem',
            color: 'var(--sb-text-muted)',
            textDecoration: 'none',
          }}
        >
          session {props.sessionId.slice(0, 8)}…
        </Link>
        <small style={{ marginLeft: 'auto', color: 'var(--sb-text-subtle)' }}>
          {formatTime(props.createdAt)}
        </small>
      </header>
      <pre
        className="approvals-list-row-args"
        style={{
          margin: '0 0 0.6rem',
          padding: '0.5rem 0.65rem',
          background: 'var(--sb-bg-subtle)',
          borderRadius: '6px',
          fontSize: '0.82rem',
          maxHeight: '200px',
          overflow: 'auto',
        }}
      >
        {JSON.stringify(props.toolArgs, null, 2)}
      </pre>
      {isDecided && props.decidedRecord !== undefined ? (
        <DecidedActions
          decision={props.decidedRecord.decision}
          decidedAt={props.decidedRecord.decidedAt}
          sessionId={props.sessionId}
        />
      ) : (
        <ApprovalActions
          ackKind={props.ackKind}
          isPending={decideMutation.isPending}
          onApprove={() => void handle('approved')}
          onReject={() => void handle('rejected')}
          sessionId={props.sessionId}
        />
      )}
      {decideMutation.error !== null && (
        <p
          className="approvals-list-row-error"
          role="alert"
          style={{ color: 'var(--sb-status-failed)', margin: '0.5rem 0 0', fontSize: '0.82rem' }}
        >
          {decideMutation.error.message}
        </p>
      )}
    </li>
  );
}

/**
 * fn-2.x — post-decide affordances. The row remains visible for
 * DECIDED_PERSIST_MS so the operator can: (a) confirm the decision
 * landed, (b) click through to the session if they want to follow up
 * on what the agent did with the approval.
 */
function DecidedActions(props: {
  readonly decision: 'approved' | 'rejected';
  readonly decidedAt: number;
  readonly sessionId: string;
}): React.ReactElement {
  const verbPast = props.decision === 'approved' ? '✓ Approved' : '✗ Rejected';
  const chipColorVar =
    props.decision === 'approved' ? 'var(--sb-status-completed)' : 'var(--sb-status-failed)';
  return (
    <div
      className="approvals-list-row-actions approvals-list-row-actions--decided"
      style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap' }}
    >
      <span
        className={`approvals-list-row-decided-chip approvals-list-row-decided-chip--${props.decision}`}
        style={{
          padding: '0.25rem 0.6rem',
          borderRadius: '999px',
          fontSize: '0.78rem',
          fontWeight: 500,
          color: chipColorVar,
          background: 'rgba(0,0,0,0.04)',
          border: `1px solid ${chipColorVar}`,
        }}
      >
        {verbPast}
      </span>
      <small style={{ color: 'var(--sb-text-muted)', fontSize: '0.75rem' }}>
        {formatTime(new Date(props.decidedAt).toISOString())}
      </small>
      <Link
        to={`/runs/${encodeURIComponent(props.sessionId)}`}
        className="approvals-list-row-open-session"
        style={{
          marginLeft: 'auto',
          padding: '0.3rem 0.7rem',
          fontSize: '0.82rem',
          border: '1px solid var(--sb-border)',
          borderRadius: '6px',
          textDecoration: 'none',
          color: 'var(--sb-text)',
          background: 'var(--sb-bg-button, var(--sb-bg-panel))',
        }}
      >
        Open session →
      </Link>
    </div>
  );
}

/**
 * Approval action buttons. Discriminates on `ackKind` to suppress
 * Approve for elicitation rows that require a form payload the UI
 * doesn't yet render (codex impl-review round 2 fix).
 *
 * **`custom_tool_result_immediate` semantics** (see
 * `docs/runbooks/hitl-operator.md`, fn-1.15 + this task's runbook
 * additions): the row is an `ask_user_question` elicitation. The
 * control-plane `POST /sessions/:id/approvals/:approval_id` REQUIRES
 * `form_data` on approve for this ack_kind (the daemon posts the form
 * payload as the custom-tool result). The v1 console does NOT render
 * the form yet; we must therefore:
 *   1. Hide Approve so an operator can't click into a guaranteed 400.
 *   2. Surface an in-row note pointing at the CLI / API path that
 *      DOES carry form data.
 *   3. Keep Reject available — rejection does not require form data;
 *      the daemon writes the deny and posts an empty/deny custom-tool
 *      result.
 * Form-renderer ships in v1.x; this gate goes away then.
 */
// Exported under an underscore-prefixed alias so the unit test can
// render it without a tRPC Provider. The production caller is the
// in-module `ApprovalRow`; the alias keeps the public surface intent
// honest (this is NOT a stable component for outside packages).
export { ApprovalActions as __unstableApprovalActionsForTests };
function ApprovalActions(props: {
  readonly ackKind:
    | 'tool_confirmation'
    | 'custom_tool_result_immediate'
    | 'custom_tool_result_deferred';
  readonly isPending: boolean;
  readonly onApprove: () => void;
  readonly onReject: () => void;
  /** fn-2.x — optional; renders an "Open session →" link if provided. */
  readonly sessionId?: string;
}): React.ReactElement {
  const elicitation = props.ackKind === 'custom_tool_result_immediate';
  return (
    <div
      className="approvals-list-row-actions"
      style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}
    >
      {elicitation ? (
        <small className="approvals-list-row-note" data-testid="approvals-elicitation-note">
          Form required — approve via <code>sbp approvals approve … --form '&lt;json&gt;'</code> or
          the REST API. The console form renderer ships in v1.x.
        </small>
      ) : (
        <button
          type="button"
          onClick={props.onApprove}
          disabled={props.isPending}
          className="approvals-list-row-approve"
        >
          {props.isPending ? 'Working…' : 'Approve'}
        </button>
      )}
      <button
        type="button"
        onClick={props.onReject}
        disabled={props.isPending}
        className="approvals-list-row-reject"
      >
        Reject
      </button>
      {props.sessionId !== undefined && (
        <Link
          to={`/runs/${encodeURIComponent(props.sessionId)}`}
          className="approvals-list-row-open-session"
          style={{
            marginLeft: 'auto',
            padding: '0.3rem 0.7rem',
            fontSize: '0.82rem',
            border: '1px solid var(--sb-border)',
            borderRadius: '6px',
            textDecoration: 'none',
            color: 'var(--sb-text-muted)',
            background: 'transparent',
          }}
          title="Inspect this session before deciding"
        >
          Open session →
        </Link>
      )}
    </div>
  );
}
